import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2, Download, Trash2 } from "lucide-react";
import { useLocation } from "wouter";

export default function DatabaseView() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  
  const [selectedTable, setSelectedTable] = useState<string | null>(null);

  const { data: tables, isLoading: isLoadingTables } = trpc.database.listTables.useQuery();
  const { data: tableData, isLoading: isLoadingData } = trpc.database.getTableData.useQuery(
    { tableName: selectedTable! },
    { enabled: !!selectedTable }
  );

  const clearTableMutation = trpc.database.clearTable.useMutation({
    onSuccess: () => {
      toast.success(`Tabela ${selectedTable} limpa com sucesso`);
    },
    onError: (err) => {
      toast.error(`Erro ao limpar tabela: ${err.message}`);
    },
  });

  const handleExportCsv = () => {
    if (!tableData || tableData.length === 0) {
      toast.error("Nenhum dado para exportar");
      return;
    }
    const headers = Object.keys(tableData[0]);
    const rows = tableData.map((row: any) =>
      headers.map((h) => {
        const val = row[h];
        if (val === null || val === undefined) return "";
        const str = typeof val === "object" ? JSON.stringify(val) : String(val);
        return `"${str.replace(/"/g, '""')}"`;
      }).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedTable}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClearTable = () => {
    if (!selectedTable) return;
    if (!window.confirm(`Tem certeza que deseja deletar TODOS os registros de "${selectedTable}"? Esta ação não pode ser desfeita.`)) return;
    clearTableMutation.mutate({ tableName: selectedTable });
  };

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }


  if (isLoadingTables) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">View Banco</h1>
        <p className="text-muted-foreground mt-2">Consultar tabelas do banco de dados</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Lista de Tabelas */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Tabelas ({tables?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {tables?.map((table) => (
              <Button
                key={table}
                variant={selectedTable === table ? "default" : "outline"}
                className="w-full justify-start"
                onClick={() => setSelectedTable(table)}
              >
                {table}
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Dados da Tabela */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>
              {selectedTable ? `Dados: ${selectedTable}` : "Selecione uma tabela"}
            </CardTitle>
            {selectedTable && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCsv}
                  disabled={!tableData || tableData.length === 0}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Exportar CSV
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleClearTable}
                  disabled={clearTableMutation.isPending}
                >
                  {clearTableMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-1" />
                  )}
                  Limpar Tabela
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selectedTable && (
              <p className="text-muted-foreground text-center py-12">
                Selecione uma tabela na lista ao lado para visualizar seus dados
              </p>
            )}

            {selectedTable && isLoadingData && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {selectedTable && !isLoadingData && tableData && (
              <div className="overflow-x-auto">
                {tableData.length === 0 ? (
                  <p className="text-muted-foreground text-center py-12">
                    Tabela vazia (sem registros)
                  </p>
                ) : (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        {Object.keys(tableData[0]).map((column) => (
                          <th
                            key={column}
                            className="text-left p-2 font-semibold bg-muted"
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((row: any, idx: number) => (
                        <tr key={idx} className="border-b hover:bg-muted/50">
                          {Object.values(row).map((value: any, colIdx: number) => (
                            <td key={colIdx} className="p-2 text-sm">
                              {value === null
                                ? <span className="text-muted-foreground italic">null</span>
                                : typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {tableData.length === 1000 && (
                  <p className="text-muted-foreground text-sm mt-4">
                    Mostrando primeiros 1000 registros
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
